/**
 * @module app.cd
 */

import { autoinject, transient } from "aurelia-framework";
import { Api } from 'app/shared/lib/api/api';
import { IQueryLoadOptions, IApiQueryResults, IApiActionResults } from 'app/shared/lib/api/api-types';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { ApiQuery } from 'app/shared/lib/essette-api';
import { IProviderSearchConfig } from 'app/cd/models/essette-interfaces';
import { Toaster } from 'app/shared/lib/toaster';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface IProviderSearchFilters {
    facilityAddress?: CD.ProviderFacilityAddress | null;
    facilityName?: string | null;
    gender?: Core.Gender | null;
    generalSearchTerm?: string | null;
    groupName?: string | null;
    identifier?: string | null;
    isContracted?: boolean | null;
    npi?: string | null;
    tax?: string | null;
    providerFirstName?: string | null;
    providerLastName?: string | null;
    specialty?: CD.ProviderSpecialty | null;
    type?: CD.AuthProviderType | null;
}
export interface IProviderGroupSearchFilters {
    groupName?: string | null;
    nationalProviderIdentifier?: string | null;
    billingCity?: string | null;
    billingState?: string | null;
    billingZip?: string | null;
    physicalCity?: string | null;
    physicalState?: string | null;
    physicalZip?: string | null;
    activeGroupsOnly?: boolean | null;
}

export function countProviderFilters(searchFilters: IProviderSearchFilters): number {
    if(!searchFilters) return 0;

    let count = 0;
    const address = searchFilters.facilityAddress || null;
    if(address) {
        if(address.street1) count += 1;
        if(address.street2) count += 1;
        if(address.city) count += 1;
        if(address.region && address.region.id) count += 1;
        if(address.postalCode) count += 1;

        if(count === 0) {
            // make sure this is cleared out if no address values
            searchFilters.facilityAddress = undefined;
        }
    }

    if(searchFilters.generalSearchTerm) { count += 1; }
    if(searchFilters.identifier) { count += 1; }
    if(searchFilters.providerFirstName) { count += 1; }
    if(searchFilters.providerLastName) { count += 1; }
    if(searchFilters.npi) { count += 1; }
    if(searchFilters.tax) { count += 1; }
    if(searchFilters.facilityName) { count += 1; }
    if(searchFilters.groupName) { count += 1; }
    if(searchFilters.gender && searchFilters.gender.id) { count += 1; }
    if(searchFilters.specialty && searchFilters.specialty.id) { count += 1; }
    if(searchFilters.type) count += 1;

    return count;
}

/** Takes an IProviderSearchFilters object and adds the filters to an ApiQuery object */
export function buildProviderLocationSearchFilters(query: ApiQuery, searchFilters: IProviderSearchFilters): void {
    if(searchFilters.generalSearchTerm) {
        query.search(searchFilters.generalSearchTerm);
    }
    else {
        if(searchFilters.identifier) {
            query.filterString('identifier.primary', searchFilters.identifier, 'startsWith');
        }
        if(searchFilters.providerFirstName) {
            query.filterString('provider.name.first', searchFilters.providerFirstName, 'startsWith');
        }
        if(searchFilters.providerLastName) {
            query.filterString('provider.name.last', searchFilters.providerLastName, 'startsWith');
        }
        if(searchFilters.npi) {
            query.filterOr(or => or
                .filterString('provider.identifier.nationalProvider', searchFilters.npi!, 'startsWith')
                .filterString('facility.identifier.nationalProvider', searchFilters.npi!, 'startsWith')
                .filterString('facility.group.identifier.nationalProvider', searchFilters.npi!, 'startsWith')
            );
        }
        if(searchFilters.tax) {
            query.filterString('facility.identifier.tax', searchFilters.tax, 'startsWith');
        }
        if(searchFilters.facilityName) {
            query.filterString('facility.description', searchFilters.facilityName, 'contains');
        }
        if(searchFilters.groupName) {
            query.filterString('facility.group.description', searchFilters.groupName, 'contains');
        }
        if(searchFilters.specialty && searchFilters.specialty.id) {
            query.filterExists('specialties', (f =>
                f.filterIdentifier('id', searchFilters!.specialty!.id!)
            ));
        }
        if(searchFilters.gender && searchFilters.gender.id) {
            query.filterIdentifier('provider.gender.id', searchFilters.gender.id);
        }
        if(searchFilters.facilityAddress) {
            query.filterExists('facility.addresses',
                ((f) => {
                    if(!(searchFilters && searchFilters.facilityAddress)) { return; }

                    const a = searchFilters.facilityAddress;
                    if(a.street1) { f.filterString('street1', a.street1, 'contains'); }
                    if(a.street2) { f.filterString('street2', a.street2, 'contains'); }
                    if(a.city) { f.filterString('city', a.city, 'startsWith'); }
                    if(a.region && a.region.id) { f.filterIdentifier('region.id', a.region.id); }
                    if(a.postalCode) { f.filterString('postalCode', a.postalCode, 'startsWith'); }
                })
            );
        }
    }
}

const defaultConfig: IProviderSearchConfig = {}

/** Provides access to API models related to Providers */
@transient()
@autoinject()
export class ProviderService {

    ////****************** Fields and Properties ************************************////
    public isConfigured: boolean = false;
    public config: IProviderSearchConfig = {};


    ////****************** Functions ************************************************////
    constructor(
        private readonly api: Api,
        private readonly logger: AppLogger,
        private readonly toaster: Toaster) {

        this.logger.source = this;
    }

    public configure(config: IProviderSearchConfig): void {
        this.config = Object.assign({}, defaultConfig, config);
        this.isConfigured = true;
    }

    ////****************** Queries **************************************************////
    public async searchProviderGroups(
        providerGroupSearchFilters: IProviderGroupSearchFilters,
        options?: Partial<IQueryLoadOptions>,
        includes: string[] = [],
        fields: string[] = []): Promise<IApiQueryResults<CD.ProviderGroup>> {

        if(!providerGroupSearchFilters) { providerGroupSearchFilters = {}; }

        const query = this.api
            .query('cd/providers/groups')
            .strategy('search');

        if(!options) {
            options = {}
        }
        if(!providerGroupSearchFilters.activeGroupsOnly) {
            options.includeInactive = true
        }

        if(providerGroupSearchFilters.groupName) {
            query.filterString('description', providerGroupSearchFilters.groupName!, 'contains')
        }

        if(providerGroupSearchFilters.nationalProviderIdentifier) {
            query.filterString('identifier.nationalProvider', providerGroupSearchFilters.nationalProviderIdentifier || null);
        }

        if(providerGroupSearchFilters.billingCity) {
            query.filterString('address.billing.city', providerGroupSearchFilters.billingCity || null, 'startsWith');
        }

        if(providerGroupSearchFilters.billingState) {
            query.filterString('address.billing.region.description', providerGroupSearchFilters.billingState || null, 'startsWith');
        }

        if(providerGroupSearchFilters.billingZip) {
            const postalCode = providerGroupSearchFilters.billingZip.replace(/\D/g, '');
            query.filterString('address.billing.postalCode', postalCode || null, 'startsWith');
        }

        if(providerGroupSearchFilters.physicalCity) {
            query.filterString('address.physical.city', providerGroupSearchFilters.physicalCity || null, 'startsWith');
        }

        if(providerGroupSearchFilters.physicalState) {
            query.filterString('address.physical.region.description', providerGroupSearchFilters.physicalState || null, 'startsWith');
        }

        if(providerGroupSearchFilters.physicalZip) {
            const postalCode = providerGroupSearchFilters.physicalZip.replace(/\D/g, '');
            query.filterString('address.physical.postalCode', postalCode || null, 'startsWith');
        }

        query.applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);
        return await this.api.get(query);
    }

    public async searchProviderLocations(searchFilters: IProviderSearchFilters, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const count = countProviderFilters(searchFilters);
        if(count === 0) {
            this.logger.warn('Invalid or missing search criteria');
            return createQueryResults('CD.ProviderLocation', [], true);
        }

        if(count > 1 && searchFilters.generalSearchTerm) { delete searchFilters.generalSearchTerm; }
        if(!!searchFilters.isContracted && !this.config.contractedSearchUrl) {
            this.logger.error('No contracted provider search url was configured');
            this.toaster.error().message('Error searching contracted providers');
            return createQueryResults('CD.ProviderLocation', [], true);
        }

        const url = !!searchFilters.isContracted ? this.config.contractedSearchUrl! : 'cd/providers/locations';
        const query = this.api
            .query(url)
            .strategy('search')
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(count > 0) {
            buildProviderLocationSearchFilters(query, searchFilters);
        }

        return await this.api.get(query);
    }

    public async searchSpecialties(searchValue: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.ProviderSpecialty>> {
        const query = this.api
            .query(`cd/providers/specialties`)
            .strategy('search')
            .applyStandardLoadOptions(options)
            .search(searchValue)
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    /**
     * Loads a list of 'CD.ProviderLocation' objects for a given provider
     * @param providerId the ID of the provider
     * @param options query load options
     * @param includes related models to include
     */
    public async loadLocationsByProvider(providerId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const query = this.api
            .query(`cd/providers/${providerId}/locations`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a list of 'CD.ProviderFacility' objects for a given provider
     * @param providerId the ID of the provider
     * @param options query load options
     * @param includes related models to include
     */
    public async loadFacilitiesByProvider(providerId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderFacility>> {
        const query = this.api
            .query(`cd/providers/${providerId}/facilities`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }


    ////****************** Loads ****************************************************////
    public async loadHealthPlans(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.HealthPlan>> {
        const query = this.api
            .query(`cd/healthplans`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadLinesOfBusiness(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.LineOfBusiness>> {
        const query = this.api
            .query(`cd/linesofbusiness`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.Provider` object
     * @param providerId the ID of the provider record to load
     * @param includes related models to include
     */
    public async loadProvider(providerId: string, includes: string[] = []): Promise<IApiQueryResults<CD.Provider>> {
        const query = this.api
            .query(`cd/providers/${providerId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadProviders(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Provider>> {
        const query = this.api
            .query(`cd/providers`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.Provider` object based on a `CD.ProviderLocation` id
     * @param providerLocationId the ID of the provider record to load
     * @param includes related models to include
     */
    public async loadProviderByLocation(providerLocationId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.Provider>> {
        const query = this.api
            .query(`cd/providers`)
            .filterExists('locations', q => q.filterIdentifier('id', providerLocationId))
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderLocation` object
     * @param providerLocationId the ID of the provider location record to load
     * @param includes related models to include
     */
    public async loadProviderLocation(providerLocationId: string, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const query = this.api
            .query(`cd/providers/locations/${providerLocationId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderFacility` object
     * @param providerFacilityId the ID of the provider facility record to load
     * @param includes related models to include
     */
    public async loadProviderFacility(providerFacilityId: string, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderFacility>> {
        const query = this.api
            .query(`cd/providers/facilities/${providerFacilityId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderContract` objects
     * @param providerGroupId the ID of the provider group record to load
     * @param includes related models to include
     */
    public async loadProviderContractsByGroup(providerGroupId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeExpired: boolean = false): Promise<IApiQueryResults<CD.ProviderGroup>> {
        const query = this.api
            .query(`cd/providers/groups/${providerGroupId}/contracts`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        if(!includeExpired) {
            query.filterOr((or) => {
                or.filterIsNull('dateRange.end', true);
                or.filterDateSpecial('dateRange.end', 'today', 'gte');
            });
        }

        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderGroup` object
     * @param providerGroupId the ID of the provider group record to load
     * @param includes related models to include
     */
    public async loadProviderGroup(providerGroupId: string, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderGroup>> {
        const query = this.api
            .query(`cd/providers/groups/${providerGroupId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderSpecialty` array
     * @param providerId the ID of the provider record to load specialties for
     * @param options query load options
     * @param includes related models to include
     */
    public async loadProviderSpecialties(providerId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderSpecialty>> {
        const query = this.api
            .query(`cd/providers/${providerId}/specialties`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
    * Loads a `CD.ProviderContact` object
    * @param providerLocationId the ID of the provider location record to load
    * @param options query load options
    * @param includes related models to include
    */
    public async loadProviderContactsByLocation(providerLocationId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderContact>> {
        const query = this.api
            .query(`cd/providers/contacts`)
            .applyStandardLoadOptions(options)
            .filterIdentifier('location.id', providerLocationId, 'eq')
            .include(...includes);
        return await this.api.get(query);
    }

    /**
    * Loads a `CD.ProviderContact` object
    * @param providerLocationId the ID of the provider location record to load
    * @param options query load options
    * @param includes related models to include
    */
    public async loadProviderContactsWithFaxByLocation(providerLocationId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderContact>> {
        const query = this.api
            .query(`cd/providers/contacts`)
            .applyStandardLoadOptions(options)
            .filterIdentifier('location.id', providerLocationId, 'eq')
            .filterIsNull('phoneNumber.fax.number', false)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Loads a `CD.ProviderContract` array
     * @param providerId the ID of the provider record to load specialties for
     * @param options query load options
     * @param includes related models to include
     */
    public async loadProviderContracts(providerId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderContract>> {
        const query = this.api
            .query(`cd/providers/${providerId}/contracts`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Returns a collection of `CD.ProviderUserContext` objects
     * @param options query load options
     * @param includes related models to include
     */
    public async loadProviderUserContexts(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderUserContext>> {
        const query = this.api
            .query(`cd/providers/users/contexts`)
            .filterSession('provider.id', '$context.id', 'CD.Provider')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Returns a collection of `CD.ProviderUserInvitationContext` objects
     * @param options query load options
     * @param includes related models to include
     */
    public async loadProviderUserInvitationContexts(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderUserInvitationContext>> {
        const query = this.api
            .query(`cd/providers/users/invitations/contexts`)
            .filterSession('provider.id', '$context.id', 'CD.Provider')
            .filterIsNull('invitation.linked.toUser.id')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
    * Returns a collection of `CD.loadProviderLocations` objects
    * @param options query load options
    * @param includes related models to include
    */
    public async loadProviderLocationsBySession(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const query = this.api
            .query(`cd/providers/locations`)
            .filterSession('provider.id', '$context.id', 'CD.Provider')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Returns a collection of `CD.ProviderLocation` records associated with a ProviderUserInvitationContext
     * @param invitationContextId
     * @param options
     * @param includes
     */
    public async loadProviderLocationsByUserInvitationContext(invitationContextId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const query = this.api
            .query(`cd/providers/users/invitations/contexts/${invitationContextId}/providerLocations`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    /**
     * Returns a collection of `CD.UserRole` records associated with a ProviderUserInvitationContext
     * @param invitationContextId
     * @param options
     * @param includes
     */
    public async loadUserRolesByUserInvitationContext(invitationContextId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.UserRole>> {
        const query = this.api
            .query(`cd/providers/users/invitations/contexts/${invitationContextId}/roles`)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadMemberProvidersByMember(memberId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = [], includeNonCurrent: boolean = true): Promise<IApiQueryResults<CD.MemberProvider>> {
        //TODO: API call that hits the fields directly for the application
        //maybe 'CD/members/{memberid}/providerhistory' ... ?
        const query = this.api
            .query(`cd/members/providers/`)
            .applyStandardLoadOptions(options)
            .filterString('member.id', memberId, 'eq')
            .include(...includes)
            .field(...fields);

        if(!includeNonCurrent) {
            query.filterDateSpecial('dateRange.start', 'today', 'lte')

            query.filterOr((or) => {
                or.filterIsNull('dateRange.end', true);
                or.filterDateSpecial('dateRange.end', 'today', 'gte');
            });
        }

        return await this.api.get(query);
    }

    public async loadMemberProvidersByPlan(planId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], includeNonCurrent: boolean = true, fields: string[] = []): Promise<IApiQueryResults<CD.MemberProvider>> {
        const query = this.api
            .query(`cd/members/providers/`)
            .applyStandardLoadOptions(options)
            .filterIdentifier('member.plan.id', planId.toString())
            .include(...includes)
            .field(...fields);

        if(!includeNonCurrent) {
            query.filterDateSpecial('dateRange.start', 'today', 'lte')

            query.filterOr((or) => {
                or.filterIsNull('dateRange.end', true);
                or.filterDateSpecial('dateRange.end', 'today', 'gte');
            });
        }

        return await this.api.get(query);
    }

    public async loadProviderLocations(providerId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderLocation>> {
        const query = this.api
            .query(`cd/providers/locations`)
            .filterString('provider.id', providerId, 'eq')
            .applyStandardLoadOptions(options)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadProviderPortalHomePages(appId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.NavigationHomePage>> {
        const query = this.api
            .query('navigation/pages/home')
            .filterIdentifier('application.id', appId)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadPortalApp(appCode: string): Promise<IApiQueryResults<Core.Application>> {
        const query = this.api
            .query('applications')
            .filterString('code', appCode, 'eq')
        return await this.api.get(query);
    }

    public async loadProviderPortalUserRoles(appId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<Core.UserRole>> {
        const query = this.api
            .query('users/roles')
            .filterIdentifier('application.id', appId)
            .applyStandardLoadOptions(options)
            .include(...includes);
        return this.api.get(query);
    }

    ////****************** Actions **************************************************////
    public async activateProviderContact(contactId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/providers/contacts/${contactId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateProviderContract(providerContractId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/providers/contracts/${providerContractId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateProviderGroup(providerGroupId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `cd/providers/groups/${providerGroupId}`, { isActive: isActive });
        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async activateProviderGroupAddress(addressId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `cd/providers/groups/addresses/${addressId}`;
        const action = this.api.action(actionCode, url, { isActive: isActive })

        if(isActive) {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async saveProviderUserInvitationContext(invitationContext: CD.ProviderUserInvitationContext): Promise<IApiActionResults> {
        const actionName = !invitationContext.id ? 'insert' : 'update';

        const action = this.api.action(actionName,
            `cd/providers/users/invitations/contexts/${actionName === 'update' ? invitationContext.id : ''}`,
            invitationContext);

        return await this.api.post(action);
    }

    public async saveProviderContact(contact: CD.ProviderContact): Promise<IApiActionResults> {
        const actionCode = contact.id ? 'update' : 'insert';
        const actionPath = `cd/providers/contacts` + (contact.id ? `/${contact.id}` : '');
        const action = this.api.action(actionCode, actionPath, contact);
        return await this.api.post(action);
    }

    public async saveProviderContract(contract: CD.ProviderContract): Promise<IApiActionResults> {
        const actionCode = contract.id ? 'update' : 'insert';
        const actionPath = `cd/providers/contracts` + (contract.id ? `/${contract.id}` : '');
        const action = this.api.action(actionCode, actionPath, contract);
        return await this.api.post(action);
    }

    public async saveProviderUserContext(invitationContext: CD.ProviderUserContext): Promise<IApiActionResults> {
        const actionName = !invitationContext.id ? 'insert' : 'update';

        const action = this.api.action(actionName,
            `cd/providers/users/contexts/${actionName === 'update' ? invitationContext.id : ''}`,
            invitationContext);

        return await this.api.post(action);
    }

    public async saveProviderGroup(providerGroup: CD.ProviderGroup): Promise<IApiActionResults> {
        const actionName = !providerGroup.id ? 'insert' : 'update';

        const action = this.api.action(actionName, `cd/providers/groups/${actionName === 'update' ? providerGroup.id : ''}`, providerGroup);

        return await this.api.post(action);
    }

    public async handleProviderGroupAddress(address: CD.ProviderGroupAddress): Promise<IApiActionResults> {
        const actionName = (!address.id && address.street1) ? 'insert' : address.street1 ? 'update' : 'deactivate';
        const action = this.api.action(actionName, `cd/providers/groups/addresses/${actionName !== 'insert' ? address.id : ''}`, address!);

        if(actionName !== 'deactivate') {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }

    }

    public async handleProviderGroupPhoneNumber(phoneNumber: CD.ProviderGroupPhoneNumber): Promise<IApiActionResults> {
        const actionName = (!phoneNumber.id && phoneNumber.number) ? 'insert' : phoneNumber.number ? 'update' : 'deactivate';
        const action = this.api.action(actionName, `cd/providers/groups/phonenumbers/${actionName !== 'insert' ? phoneNumber.id : ''}`, phoneNumber!);

        if(actionName !== 'deactivate') {
            return await this.api.post(action);
        } else {
            return await this.api.delete(action);
        }
    }

    public async loadProviderGroupPhoneNumberTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderGroupPhoneNumberType>> {
        const query = this.api
            .query('cd/providers/groups/phoneNumbers/types')
            .applyStandardLoadOptions(options)
            .include(...includes)

        return await this.api.get(query);
    }

    public async loadAddressByProviderGroup(groupId: string, addressTypeId: string, options?: Partial<IQueryLoadOptions>, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<CD.ProviderGroupAddress>> {
        const query = this.api
            .query('cd/providers/groups/addresses')
            .applyStandardLoadOptions(options)
            .filterString('group.id', groupId, 'eq')
            .filterString('type.id', addressTypeId, 'eq')
            .include(...includes)
            .field(...fields);

        return await this.api.get(query);
    }

    public async loadProviderGroupAddressTypes(options?: Partial<IQueryLoadOptions>, includes: string[] = []): Promise<IApiQueryResults<CD.ProviderGroupAddressType>> {
        const query = this.api
            .query('cd/providers/groups/addresses/types')
            .applyStandardLoadOptions(options)
            .include(...includes)

        return await this.api.get(query);
    }

    public async deactivateProviderUserContext(userContext: CD.ProviderUserContext): Promise<IApiActionResults> {
        const action = this.api.action("delete", `cd/providers/users/contexts/${userContext.id}`, userContext);

        return await this.api.delete(action);
    }

    public async deactivateProviderUserInvitation(invitation: CD.ProviderUserInvitation): Promise<IApiActionResults> {
        const action = this.api.action("deactivate", `cd/providers/users/invitations/${invitation.id}`, invitation);

        return await this.api.delete(action);
    }

    public async resendProviderUserInvitation(invitation: CD.ProviderUserInvitation): Promise<IApiActionResults> {
        const action = this.api.action("resend", `cd/providers/users/invitations/${invitation.id}`, invitation);

        return await this.api.post(action);
    }
}
